/**
 * 通用动态库加载器
 * 用于按需动态加载第三方JavaScript库和CSS
 */
export class DynamicLibLoader {
  private static loadedLibs = new Set<string>();
  private static loadingPromises = new Map<string, Promise<void>>();

  /**
   * 动态加载CSS文件
   * @param url CSS文件URL
   * @param timeout 超时时间（毫秒），默认10秒
   * @returns Promise
   */
  static async loadCSS(url: string, timeout: number = 10000): Promise<void> {
    // 如果已经加载过，直接返回
    if (this.loadedLibs.has(url)) {
      return Promise.resolve();
    }

    // 如果正在加载中，返回现有的Promise
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!;
    }

    const loadPromise = new Promise<void>((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.type = "text/css";
      link.href = url;

      const timer = setTimeout(() => {
        this.loadingPromises.delete(url);
        reject(new Error(`CSS loading timeout: ${url}`));
      }, timeout);

      link.onload = () => {
        clearTimeout(timer);
        this.loadedLibs.add(url);
        this.loadingPromises.delete(url);
        console.log(`[DynamicLoader] Successfully loaded CSS: ${url}`);
        resolve();
      };

      link.onerror = () => {
        clearTimeout(timer);
        this.loadingPromises.delete(url);
        reject(new Error(`Failed to load CSS: ${url}`));
      };

      document.head.appendChild(link);
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  /**
   * 动态加载单个脚本文件
   * @param url 脚本URL
   * @param globalVar 预期的全局变量名（可选，用于验证加载成功）
   * @param timeout 超时时间（毫秒），默认15秒
   * @returns Promise
   */
  static async loadScript(
    url: string,
    globalVar?: string,
    timeout: number = 15000
  ): Promise<void> {
    // 如果已经加载过，直接返回
    if (this.loadedLibs.has(url)) {
      return Promise.resolve();
    }

    // 如果正在加载中，返回现有的Promise
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!;
    }

    const loadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;

      const timer = setTimeout(() => {
        this.loadingPromises.delete(url);
        reject(new Error(`Script loading timeout: ${url}`));
      }, timeout);

      script.onload = () => {
        clearTimeout(timer);
        this.loadedLibs.add(url);
        this.loadingPromises.delete(url);

        // 如果指定了全局变量，验证是否存在
        if (globalVar && !(window as any)[globalVar]) {
          reject(new Error(`Global variable ${globalVar} not found after loading ${url}`));
          return;
        }

        console.log(`[DynamicLoader] Successfully loaded: ${url}`);
        resolve();
      };

      script.onerror = () => {
        clearTimeout(timer);
        this.loadingPromises.delete(url);
        reject(new Error(`Failed to load script: ${url}`));
      };

      document.head.appendChild(script);
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  /**
   * 批量并行加载多个脚本
   * @param scripts 脚本配置数组
   * @returns Promise
   */
  static async loadScripts(
    scripts: Array<{
      url: string;
      globalVar?: string;
      timeout?: number;
    }>
  ): Promise<void> {
    const loadPromises = scripts.map((script) =>
      this.loadScript(script.url, script.globalVar, script.timeout)
    );

    await Promise.all(loadPromises);
  }

  /**
   * 加载秒验所需的依赖库
   * 包括：axios、crypto-js、jsencrypt、秒验SDK
   */
  static async loadMiaoYanDependencies(): Promise<void> {
    console.log("[DynamicLoader] 开始加载秒验依赖...");

    try {
      // 检查是否已经通过npm方式全局化了这些库
      const hasAxios = !!(window as any).axios;
      const hasCryptoJS = !!(window as any).CryptoJS;
      const hasJSEncrypt = !!(window as any).JSEncrypt;

      const scriptsToLoad = [];

      // 只加载尚未存在的依赖
      if (!hasAxios) {
        scriptsToLoad.push({
          url: "https://unpkg.com/axios/dist/axios.min.js",
          globalVar: "axios",
        });
      }

      if (!hasCryptoJS) {
        scriptsToLoad.push({
          url: "https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.1.1/crypto-js.min.js",
          globalVar: "CryptoJS",
        });
      }

      if (!hasJSEncrypt) {
        scriptsToLoad.push({
          url: "https://cdnjs.cloudflare.com/ajax/libs/jsencrypt/3.3.2/jsencrypt.min.js",
          globalVar: "JSEncrypt",
        });
      }

      // 并行加载所有缺失的依赖
      if (scriptsToLoad.length > 0) {
        await this.loadScripts(scriptsToLoad);
      }

      // 加载秒验SDK（依赖于上面的库，所以要等上面加载完成）
      await this.loadScript("/js/combined_zztfly.umd.js", "verificationInterface");

      console.log("[DynamicLoader] 秒验依赖加载完成");
    } catch (error) {
      console.error("[DynamicLoader] 秒验依赖加载失败:", error);
      throw error;
    }
  }

  /**
   * 检查秒验SDK是否已加载
   */
  static isMiaoYanLoaded(): boolean {
    return !!(window as any).verificationInterface;
  }

  /**
   * 加载闪验（创蓝）所需的依赖
   * 包括：CSS样式 + 闪验SDK
   */
  static async loadShanyanDependencies(): Promise<void> {
    console.log("[DynamicLoader] 开始加载闪验依赖...");

    try {
      // 检查是否已经加载
      const hasShanyanSDK = !!(window as any).clshanyansdk;

      if (hasShanyanSDK) {
        console.log("[DynamicLoader] 闪验SDK已存在，跳过加载");
        return;
      }

      // 并行加载 CSS 和 JS
      await Promise.all([
        this.loadCSS("/css/one_key_style.css"),
        this.loadScript("/js/shanyan2.0.0.4.js", "clshanyansdk"),
      ]);

      console.log("[DynamicLoader] 闪验依赖加载完成");
    } catch (error) {
      console.error("[DynamicLoader] 闪验依赖加载失败:", error);
      throw error;
    }
  }

  /**
   * 检查闪验SDK是否已加载
   */
  static isShanyanLoaded(): boolean {
    return !!(window as any).clshanyansdk;
  }

  /**
   * 检查指定的全局变量是否存在
   */
  static isGlobalVarLoaded(varName: string): boolean {
    return !!(window as any)[varName];
  }

  /**
   * 获取已加载的库列表
   */
  static getLoadedLibs(): string[] {
    return Array.from(this.loadedLibs);
  }

  /**
   * 清除加载记录（主要用于测试）
   */
  static clearLoadedLibs(): void {
    this.loadedLibs.clear();
    this.loadingPromises.clear();
  }
}
